import * as yup from 'yup';
import React, { useMemo, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/lab/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ButtonLoading from '@mui/lab/LoadingButton';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import ModalDeleteComponent, { ModalDeleteState } from 'components/ModalComponent/ModalDeleteComponent';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  useDeleteCustomRule,
  useGetAttributeRuleColorDropdown,
  useGetCustomRuleDetails,
  useGetCustomRuleDropdownFormData,
  useUpsertCustomRule,
} from 'services/v1/Tenant/AROKMS/CustomRuleService';
import {
  AutoCompleteWithType,
  CustomRuleConditionDropdownItem,
  CustomRuleListItem,
  RequestUpsertCustomRule,
} from 'types/api/Tenant/AROKMS/CustomRuleTypes';
import { ConditionField } from './Conditional';
import { useSubjectUsedDropdown } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { SUBJECT_TYPE } from 'constant/DataInputConstant';

export const CUSTOM_RULE_OPERATOR_VALUE = {
  EQUALS: 'Equals',
  NOT_EQUALS: 'Not Equals',
  NOT_EMPTY: 'Not Empty',
};

export const CUSTOM_RULE_ACTION = {
  SHOW: 'SHOW',
  HIDE: 'HIDE',
  RENAME_FIELD: 'RENAME_FIELD',
  CELL_FORMAT_COLOR_REF_TABLE: 'CELL_FORMAT_COLOR_REF_TABLE',
};

const autocompleteStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};

const textInputStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const sectionCardStyle = {
  bgcolor: '#FFFFFF',
  border: '1px solid #E3EBF6',
  borderRadius: 1.5,
  p: 2.5,
  mb: 2,
};

const conditionRowStyle = {
  bgcolor: '#FFFFFF',
  border: '1px solid #E3EBF6',
  borderRadius: 1.5,
  px: 2,
  py: 1.5,
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: '#42BB93',
    boxShadow: '0 1px 4px rgba(66, 187, 147, 0.1)',
  },
};

const conditionConnectorStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  py: 0.5,
};

const andChipStyle = {
  fontSize: '12px',
  fontWeight: 700,
  height: '24px',
  bgcolor: '#E8F5E9',
  color: '#2E7D32',
  border: '1px solid #A5D6A7',
};

const orChipStyle = {
  fontSize: '12px',
  fontWeight: 700,
  height: '24px',
  bgcolor: '#FFF3E0',
  color: '#E65100',
  border: '1px solid #FFCC80',
};

const ACTION_ICON_MAP: Record<string, React.ReactNode> = {
  SHOW: <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />,
  HIDE: <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />,
  RENAME_FIELD: <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 18 }} />,
  CELL_FORMAT_COLOR_REF_TABLE: <PaletteOutlinedIcon sx={{ fontSize: 18 }} />,
};

const actionGridStyle = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '10px',
};

const actionButtonBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 1,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  px: 2,
  py: 1.25,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  bgcolor: '#FFFFFF',
  '&:hover': {
    bgcolor: '#F3F6F9',
    borderColor: '#42BB93',
  },
};

const actionButtonSelected = {
  ...actionButtonBase,
  bgcolor: '#E8F5E9',
  borderColor: '#42BB93',
  boxShadow: '0 0 0 1px #42BB93',
};

const summaryContainerStyle = {
  p: 2,
  bgcolor: '#F8FAFC',
  border: '1px solid #E3EBF6',
  borderRadius: 1.5,
};

const footerStyle = {
  px: 3,
  py: 2,
  borderTop: '1px solid #E3EBF6',
  bgcolor: '#FBFBFB',
};

export interface CustomRuleDetailsProps {
  style?: React.CSSProperties;
  subjectId?: string | number;
  editRow?: CustomRuleListItem | null;
  onBackToList?: () => void;
}

interface FormikInitialValues {
  attribute: AutoCompleteWithType | null;
  ruleName: string;
  parameterValue?: string;
  cellColorSubjectRef: AutoCompleteItem | null;
  cellColorAttributeRef: AutoCompleteItem | null;
  conditions: {
    id: string;
    operator: string | null;
    field: string;
    operatorValue: string;
    operatorType: string;
    value: string;
  }[];
  action: AutoCompleteItem | null;
}

let conditionIdCounter = 0;
function generateConditionId() {
  conditionIdCounter += 1;
  return `condition-${Date.now()}-${conditionIdCounter}`;
}

const formikInitialValues: FormikInitialValues = {
  attribute: null,
  ruleName: '',
  cellColorAttributeRef: null,
  cellColorSubjectRef: null,
  conditions: [
    {
      id: generateConditionId(),
      operator: null,
      field: '',
      operatorValue: '',
      operatorType: '',
      value: '',
    },
  ],
  action: null,
};

const validationSchema = yup.object({
  attribute: yup
    .object()
    .nullable()
    .shape({
      label: yup.string().required('Attribute is required'),
      value: yup.string().required('Attribute is required'),
    })
    .required('Attribute is required'),

  conditions: yup.lazy((_, context) => {
    const actionValue = context.parent.action?.value;
    if (actionValue === 'CELL_FORMAT_COLOR_REF_TABLE') {
      return yup
        .array()
        .of(
          yup.object().shape({
            field: yup.string().nullable(),
            operator: yup.string().nullable(),
            value: yup.string().when('operator', {
              is: CUSTOM_RULE_OPERATOR_VALUE.NOT_EMPTY,
              then: yup.string().nullable().optional(),
              otherwise: yup.string().nullable().optional(),
            }),
          }),
        )
        .optional();
    } else {
      return yup
        .array()
        .of(
          yup.object().shape({
            field: yup.string().required('Field is required'),
            operator: yup.string().required('Operator is required'),
            value: yup.string().when('operator', {
              is: CUSTOM_RULE_OPERATOR_VALUE.NOT_EMPTY,
              then: yup.string().nullable().optional(),
              otherwise: yup.string().required('Value is required'),
            }),
          }),
        )
        .required('Conditions are required');
    }
  }),

  action: yup
    .object()
    .nullable()
    .shape({
      label: yup.string().required('Action is required'),
      value: yup.string().required('Action is required'),
    })
    .required('Action is required'),
});

export function CustomRuleDetails(props: CustomRuleDetailsProps) {
  const { style, subjectId, editRow, onBackToList = () => {} } = props;

  const [modalDeleteState, setModalDeleteState] = useState<ModalDeleteState>({
    message: 'Are you sure you want to delete this rule?',
    open: false,
  });
  const { data: dropdownFormData, isLoading: isLoadingDropdownForm } = useGetCustomRuleDropdownFormData(subjectId);
  const { data: customRuleDetails, isLoading: isLoadingDetail } = useGetCustomRuleDetails(editRow?.id);
  const { mutate: upsertCustomRule, isLoading: isUpdating } = useUpsertCustomRule(subjectId);
  const { mutate: deleteCustomRule } = useDeleteCustomRule();
  const isLoading = isLoadingDropdownForm || isLoadingDetail;

  const { data: subjectDropdownData } = useSubjectUsedDropdown({
    enabled: true,
    subjectType: [SUBJECT_TYPE.TABLE],
  });

  const formik = useFormik({
    initialValues: formikInitialValues,
    validationSchema: validationSchema,
    onSubmit: () => {
      handleOnSubmit();
    },
  });

  const { data: attribtueColorDropdown, isLoading: isLoadingAttributeColor } = useGetAttributeRuleColorDropdown(
    formik.values.cellColorSubjectRef?.value,
  );

  const addCondition = (operator: string) => {
    const newCondition = {
      id: generateConditionId(),
      operator: null,
      operatorType: operator,
      field: '',
      operatorValue: '',
      value: '',
    };
    formik.setFieldValue('conditions', [...formik.values.conditions, newCondition]);
  };

  const removeCondition = (index: number) => {
    const newConditions = [...formik.values.conditions];
    newConditions.splice(index, 1);
    formik.setFieldValue('conditions', newConditions);
  };

  const isLastCondition = (index: number) => index === formik.values.conditions.length - 1;
  const conditionCount = formik.values.conditions.length;
  const isCellColorAction = formik.values.action?.value === CUSTOM_RULE_ACTION.CELL_FORMAT_COLOR_REF_TABLE;

  const attributeOptions: AutoCompleteItem[] = useMemo(() => {
    if (dropdownFormData) {
      return dropdownFormData.data.attributeOptions;
    }
    return [];
  }, [dropdownFormData]);

  const fieldData: CustomRuleConditionDropdownItem[] = useMemo(() => {
    if (dropdownFormData) {
      return dropdownFormData.data.allAttributeOptions;
    }
    return [];
  }, [dropdownFormData]);

  const handleOnSubmit = () => {
    const payload: RequestUpsertCustomRule = {
      action: formik.values.action?.value?.toString() || '',
      enabled: true,
      parameterValue: formik.values.parameterValue,
      ruleName: formik.values.ruleName || '',
      tableDefinitionId: formik.values.attribute?.value?.toString() || '',
      ruleCondition: JSON.stringify(formik.values.conditions),
    };

    if (
      formik.values.action?.value === CUSTOM_RULE_ACTION.CELL_FORMAT_COLOR_REF_TABLE &&
      formik.values.cellColorAttributeRef &&
      formik.values.cellColorSubjectRef
    ) {
      const colorReferenceDefinition = {
        tableDefinitionId: formik.values.cellColorAttributeRef.value,
      };
      payload.ruleCondition = JSON.stringify(colorReferenceDefinition);
    }

    if (editRow != null) {
      payload.id = editRow.id;
    }

    upsertCustomRule(payload, {
      onSuccess: () => {
        onBackToList && onBackToList();
        toast.success('Custom rule has been saved successfully');
        formik.resetForm();
      },
    });
  };

  const handleOnShowDeleteConfirmation = () => {
    const message = `Are you sure you want to delete rule '${formik.values.ruleName}'?`;
    setModalDeleteState({ ...modalDeleteState, open: true, message });
  };

  const handleOnDeleteData = () => {
    if (editRow?.id) {
      deleteCustomRule(editRow?.id, {
        onSuccess: () => {
          setModalDeleteState({ ...modalDeleteState, open: false });
          onBackToList && onBackToList();
          toast.success('Custom rule has been deleted successfully');
          formik.resetForm();
        },
      });
    }
  };

  const generateDescription = () => {
    if (isCellColorAction) {
      if (formik.values.cellColorSubjectRef && formik.values.cellColorAttributeRef) {
        return `Attribute "${formik.values.attribute?.label}" will be colored based on the data from the "${formik.values.cellColorAttributeRef.label}" column in the "${formik.values.cellColorSubjectRef.label}" table.`;
      }
      return null;
    }

    const stack: string[] = [];
    let tempGroup: string[] = [];

    formik.values.conditions.forEach((condition, index) => {
      const { field, operatorValue, value, operator } = condition;
      if (!field || !operator) return;

      const valueInDropdown = dropdownFormData?.data.allAttributeOptions.find((item) => item.fieldName === field);
      const valueOption = valueInDropdown?.options?.find((item) => item.value === value);
      // @ts-ignore
      const renderValue = valueOption ? valueOption?.label : value;

      tempGroup.push(
        `the ${field} ${operatorValue} is ${operator} ${
          operatorValue !== CUSTOM_RULE_OPERATOR_VALUE.NOT_EMPTY ? renderValue : ''
        }`,
      );

      if (index < formik.values.conditions.length - 1) {
        const nextOperatorType = formik.values.conditions[index + 1].operatorType;

        if (nextOperatorType === 'OR') {
          if (tempGroup.length > 1) {
            stack.push('(' + tempGroup.join(' AND ') + ')');
          } else {
            stack.push(tempGroup[0]);
          }
          tempGroup = [];
        }
      } else {
        if (tempGroup.length > 1) {
          stack.push('(' + tempGroup.join(' AND ') + ')');
        } else if (tempGroup.length === 1) {
          stack.push(tempGroup[0]);
        }
      }
    });

    if (stack.length === 0) return null;
    const description = stack.join(' OR ');
    return `Attribute "${formik.values.attribute?.label}" will be ${formik?.values?.action?.label} when ${description}.`;
  };

  useEffect(() => {
    if (!editRow) formik.resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRow]);

  useEffect(() => {
    if (customRuleDetails && customRuleDetails.data) {
      try {
        const initialEditValues: FormikInitialValues = {
          cellColorAttributeRef: null,
          cellColorSubjectRef: null,
          action: customRuleDetails.data.action,
          attribute: customRuleDetails.data.tableDefinition as AutoCompleteWithType,
          ruleName: customRuleDetails.data.ruleName,
          conditions: JSON.parse(customRuleDetails.data.ruleCondition),
        };
        formik.setValues(initialEditValues);
      } catch {
        // If stored rule condition is malformed, keep default
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customRuleDetails]);

  const isEligibleToSubmit = useMemo(() => {
    if (formik.values.attribute?.type === 'MANDATORY' && formik.values.action?.value === 'HIDE') {
      return false;
    }
    return validationSchema.isValidSync(formik.values);
  }, [formik.values]);

  const isValidSchema = useMemo(() => {
    return validationSchema.isValidSync(formik.values);
  }, [formik.values]);

  const ruleSummaryDesc = useMemo(() => {
    return generateDescription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  if (isLoading) {
    return (
      <Stack sx={{ height: 400 }}>
        <ActivityIndicator />
      </Stack>
    );
  }

  return (
    <>
      <Stack style={style} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* ── Scrollable form content ── */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
          {/* Section 1: Basic Info */}
          <Box sx={sectionCardStyle}>
            <Typography variant='input-label' fontWeight={700} fontSize={14} mb={1.5} sx={{ color: '#3B4797' }}>
              Basic Information
            </Typography>
            <Stack direction='row' gap={3}>
              <Stack flex={1}>
                <Typography variant='input-label' fontWeight={700} mb={0.5}>
                  Rule Name
                </Typography>
                <Typography variant='input-label-gray' fontSize={12} mb={0.5}>
                  Provide a name for the rule you are creating.
                </Typography>
                <TextField
                  sx={textInputStyles}
                  name='ruleName'
                  hiddenLabel
                  variant='outlined'
                  size='small'
                  onBlur={formik.handleBlur}
                  error={formik.touched.ruleName && Boolean(formik.errors.ruleName)}
                  helperText={formik.touched.ruleName && formik.errors.ruleName}
                  value={formik.values.ruleName}
                  placeholder='e.g. Hide Column A'
                  onChange={formik.handleChange}
                />
              </Stack>
              <Stack flex={1}>
                <Typography variant='input-label' fontWeight={700} mb={0.5}>
                  Attribute
                </Typography>
                <Typography variant='input-label-gray' fontSize={12} mb={0.5}>
                  Select the attribute this rule applies to.
                </Typography>
                <Autocomplete
                  options={attributeOptions}
                  getOptionLabel={(option) => option.label}
                  value={formik.values.attribute}
                  sx={autocompleteStyles}
                  onChange={(_, newValue) => {
                    formik.setFieldValue('attribute', newValue);
                  }}
                  clearIcon={null}
                  renderInput={(params) => <TextField {...params} placeholder='Select Attribute...' />}
                />
              </Stack>
            </Stack>
          </Box>

          {/* Section 2: Action */}
          <Stack sx={sectionCardStyle} direction='column'>
            <Typography variant='input-label' fontWeight={700} fontSize={14} mb={0.5} sx={{ color: '#3B4797' }}>
              Action
            </Typography>
            <Typography variant='input-label-gray' fontSize={12} mb={1.5}>
              Choose the action executed when conditions are met.
            </Typography>
            <Box sx={actionGridStyle}>
              {(dropdownFormData?.data?.actionOptions || []).map((option) => {
                const isSelected = formik.values.action?.value === option.value;
                return (
                  <Box
                    key={option.value}
                    sx={isSelected ? actionButtonSelected : actionButtonBase}
                    onClick={() => formik.setFieldValue('action', option)}
                  >
                    <Box sx={{ color: isSelected ? '#2E7D32' : '#828C99', display: 'flex', alignItems: 'center' }}>
                      {ACTION_ICON_MAP[option.value?.toString()] || null}
                    </Box>
                    <Typography
                      fontSize={13}
                      fontWeight={isSelected ? 700 : 600}
                      color={isSelected ? '#2E7D32' : '#667085'}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {formik.values.action?.value === CUSTOM_RULE_ACTION.RENAME_FIELD && (
              <Stack width='50%' mt={2}>
                <Typography variant='input-label-gray' fontSize={12} mb={0.5}>
                  Provide a name for overriding the default field name.
                </Typography>
                <TextField
                  sx={textInputStyles}
                  name='parameterValue'
                  hiddenLabel
                  variant='outlined'
                  size='small'
                  onBlur={formik.handleBlur}
                  error={formik.touched.parameterValue && Boolean(formik.errors.parameterValue)}
                  helperText={formik.touched.parameterValue && formik.errors.parameterValue}
                  value={formik.values.parameterValue}
                  placeholder='e.g Description'
                  onChange={formik.handleChange}
                />
              </Stack>
            )}
          </Stack>

          {/* Section 3a: Cell Color Reference (conditional) */}
          {isCellColorAction && (
            <Box sx={sectionCardStyle}>
              <Typography variant='input-label' fontWeight={700} fontSize={14} mb={1.5} sx={{ color: '#3B4797' }}>
                Cell Color Reference
              </Typography>
              <Stack direction='row' gap={3}>
                <Stack flex={1}>
                  <Typography variant='input-label-gray' fontSize={12} mb={0.5}>
                    Select a subject to reference for cell coloring.
                  </Typography>
                  <Autocomplete
                    options={subjectDropdownData?.data || []}
                    getOptionLabel={(option) => option.label}
                    value={formik.values.cellColorSubjectRef}
                    sx={autocompleteStyles}
                    onChange={(_, newValue) => {
                      formik.setFieldValue('cellColorSubjectRef', newValue);
                    }}
                    clearIcon={null}
                    renderInput={(params) => <TextField {...params} placeholder='Select Subject...' />}
                  />
                </Stack>
                <Stack flex={1}>
                  <Typography variant='input-label-gray' fontSize={12} mb={0.5}>
                    Select an attribute to use for cell coloring.
                  </Typography>
                  <Autocomplete
                    disabled={!formik.values.cellColorSubjectRef}
                    options={attribtueColorDropdown?.data || []}
                    loading={isLoadingAttributeColor}
                    getOptionLabel={(option) => option.label}
                    value={formik.values.cellColorAttributeRef}
                    sx={autocompleteStyles}
                    onChange={(_, newValue) => {
                      formik.setFieldValue('cellColorAttributeRef', newValue);
                    }}
                    clearIcon={null}
                    renderInput={(params) => <TextField {...params} placeholder='Select Color Attribute...' />}
                  />
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Section 3b: Conditions (when not cell color action) */}
          {!isCellColorAction && (
            <Box sx={sectionCardStyle}>
              <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Typography variant='input-label' fontWeight={700} fontSize={14} sx={{ color: '#3B4797' }}>
                    Conditions
                  </Typography>
                  <Chip
                    label={`${conditionCount} ${conditionCount === 1 ? 'rule' : 'rules'}`}
                    size='small'
                    sx={{ fontSize: '12px', height: '22px', bgcolor: '#E3EBF6', color: '#3B4797', fontWeight: 600 }}
                  />
                </Stack>
                <Typography variant='input-label-gray' fontSize={12}>
                  When the following input values are met...
                </Typography>
              </Stack>

              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={0}>
                  {formik.values.conditions.map((condition, index) => (
                    <React.Fragment key={condition.id}>
                      {/* AND/OR Connector */}
                      {index !== 0 && (
                        <Box sx={conditionConnectorStyle}>
                          {condition.operatorType === 'AND' && <Chip label='AND' size='small' sx={andChipStyle} />}
                          {condition.operatorType === 'OR' && <Chip label='OR' size='small' sx={orChipStyle} />}
                        </Box>
                      )}

                      {/* Condition Card */}
                      <Box sx={conditionRowStyle}>
                        <Stack direction='row' alignItems='center' spacing={1}>
                          {conditionCount > 1 && (
                            <Typography
                              variant='input-label-gray'
                              fontSize={12}
                              fontWeight={700}
                              sx={{ minWidth: 20, textAlign: 'center' }}
                            >
                              {index + 1}
                            </Typography>
                          )}
                          <Box sx={{ flex: 1 }}>
                            <ConditionField
                              condition={condition}
                              index={index}
                              isLast={isLastCondition(index)}
                              fieldData={fieldData}
                              formik={formik}
                              onRemove={removeCondition}
                              onAdd={addCondition}
                            />
                          </Box>
                        </Stack>
                      </Box>
                    </React.Fragment>
                  ))}
                </Stack>
              </form>
            </Box>
          )}

          {/* Rule Summary */}
          {isValidSchema && ruleSummaryDesc && (
            <Box sx={summaryContainerStyle}>
              <Stack direction='row' alignItems='center' spacing={1} mb={1}>
                <SummarizeOutlinedIcon sx={{ fontSize: 18, color: '#3B4797' }} />
                <Typography variant='input-label' fontWeight={700} fontSize={13}>
                  Rule Summary
                </Typography>
              </Stack>
              <Alert variant='outlined' severity='info' sx={{ fontSize: 13 }}>
                {ruleSummaryDesc}
              </Alert>
            </Box>
          )}

          {/* Warnings */}
          {isValidSchema &&
            formik.values.attribute?.type === 'MANDATORY' &&
            formik.values.action?.value === CUSTOM_RULE_ACTION.HIDE && (
              <Alert variant='outlined' severity='error' sx={{ mt: 2, fontSize: 13 }}>
                {`Warning: The 'Hide' action is not applicable to mandatory attributes. '${formik.values.attribute?.label}' is a mandatory attribute and must always be displayed.`}
              </Alert>
            )}

          {isValidSchema &&
            formik.values.attribute?.type === 'MANDATORY' &&
            formik.values.action?.value === CUSTOM_RULE_ACTION.SHOW && (
              <Alert variant='outlined' severity='error' sx={{ mt: 2, fontSize: 13 }}>
                {`Warning: The 'Show' action cannot be applied to mandatory attributes. This restriction is in place to ensure that essential attributes, such as '${formik.values.attribute?.label}', are always displayed.`}
              </Alert>
            )}
        </Box>

        {/* ── Footer ── */}
        <Box sx={footerStyle}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            {/* Left side - Delete (only in edit mode) */}
            <Box>
              {Boolean(editRow) && (
                <ButtonLoading
                  onClick={handleOnShowDeleteConfirmation}
                  variant='main-table-panel-borderless'
                  startIcon={<DeleteOutlineRoundedIcon />}
                  sx={{ fontSize: 13, color: '#BB4942' }}
                >
                  Delete Rule
                </ButtonLoading>
              )}
            </Box>

            {/* Right side - Cancel & Save */}
            <Stack direction='row' spacing={1.5}>
              <ButtonLoading
                variant='main-table-panel-border'
                disabled={isUpdating}
                onClick={onBackToList}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </ButtonLoading>
              <ButtonLoading
                variant='main-table-panel'
                disabled={!isEligibleToSubmit}
                loading={isUpdating}
                onClick={handleOnSubmit}
                sx={{ minWidth: 140 }}
              >
                {isUpdating ? 'Saving...' : 'Save Rule'}
              </ButtonLoading>
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <ModalDeleteComponent
        errorMessage={modalDeleteState.errorMessage}
        isError={modalDeleteState.isError}
        visible={modalDeleteState.open}
        title={null}
        message={modalDeleteState.message}
        onApprove={handleOnDeleteData}
        onClose={() => setModalDeleteState({ ...modalDeleteState, open: false })}
        onCancel={() => setModalDeleteState({ ...modalDeleteState, open: false })}
      />
    </>
  );
}
